// Ambient inline text editing for a post's body copy (caption / message / text / title /
// description). An uncontrolled contentEditable span: it sets its text imperatively so React
// never re-renders the editing node (which would jump the caret mid-typing — the classic
// controlled-contentEditable pitfall). Edits debounce-commit via onCommit; the caller wires
// that to updateCreativeContent, which persists + re-scores. AI revises arrive as a changed
// initialValue and are reflected only while the field is not focused.

import { useEffect, useMemo, useRef } from 'react'
import { debounce } from 'lodash'

export function EditableInlineText({
  initialValue,
  onCommit,
  className,
  ariaLabel,
  placeholder,
}: {
  initialValue: string
  onCommit: (text: string) => void
  className?: string
  ariaLabel?: string
  placeholder?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)

  // Stable debounce: built once, always calls the latest onCommit via a ref, so a parent
  // re-render (score update, AI tick, store mirror) can't rebuild + cancel a pending commit.
  const onCommitRef = useRef(onCommit)
  onCommitRef.current = onCommit
  // Last text the node was SEEDED with or COMMITTED — the no-op baseline for
  // blur. Comparing against `initialValue` instead would be wrong: callers
  // pass DERIVED values (hashtag-stripped captions), so prop ≠ store is the
  // steady state and a real edit could read as "unchanged".
  const committedRef = useRef<string>(initialValue)
  const commit = useMemo(
    () =>
      debounce((t: string) => {
        committedRef.current = t
        onCommitRef.current(t)
      }, 500),
    []
  )
  // Flush (not cancel) on unmount so a pending edit isn't lost when the field unmounts before a
  // blur fires (e.g. a mobile bottom sheet closing while the caption was being typed).
  useEffect(() => () => commit.flush(), [commit])

  // Reflect external changes (AI revise / regenerate) into the node, but never while the user
  // is typing in it — overwriting a focused node would move the caret.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (document.activeElement === el) return
    if (el.innerText !== initialValue) el.innerText = initialValue
    committedRef.current = initialValue
  }, [initialValue])

  // Seed the initial text once on mount (no children → React won't manage the content).
  useEffect(() => {
    const el = ref.current
    if (el && el.innerText !== initialValue) el.innerText = initialValue
    committedRef.current = initialValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <span
      ref={ref}
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline="true"
      data-placeholder={placeholder}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      className={`empty:before:pointer-events-none empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)] ${className ?? ''}`}
      // innerText (not textContent): contentEditable inserts <div>/<br> on Enter, which
      // textContent flattens into one line — innerText returns the rendered breaks as \n so
      // multi-paragraph captions survive an edit (and round-trip with the seed/sync above).
      onInput={e => commit(e.currentTarget.innerText ?? '')}
      onBlur={e => {
        commit.cancel()
        // No-op guard: blur without a real change (click-in/click-out, Escape
        // revert) must NOT commit — an unconditional commit here registered a
        // phantom "user edit" server-side (canonical revision bump + variant
        // pin/fan-out) for merely focusing the text.
        const text = e.currentTarget.innerText ?? ''
        if (text === committedRef.current) return
        committedRef.current = text
        onCommitRef.current(text)
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault()
          commit.cancel()
          if (ref.current) ref.current.innerText = initialValue
          ref.current?.blur()
        }
      }}
      // Don't let a click inside the editable bubble up to a parent section selector.
      onClick={e => e.stopPropagation()}
    />
  )
}
